import { useState, useContext, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import ExperienceContext from "./ExperienceContext";
import {
	TextInput,
	Switch,
	Button,
	Tabs,
	Input,
	Image,
	Modal,
	Group,
	Container,
	Loader,
	Text,
	ScrollArea,
	Grid,
	Pagination,
} from "@mantine/core";
import uploadIcon from "../../static/svg/upload.svg";
import {
	showErrorNotification,
	showSuccessNotification,
} from "../../utils/Notifications";
import { getAssets } from "../../API/getAssets";
import uploadMedia from "../../API/uploadMedia";
import { CDN_URL } from "../../constant";
function AddPanoModal({ isOpen, setIsAddModalOpen, closeControls }) {
	const user = useSelector((state) => state.userInformationReducer.users);
	const [activeTab, setActiveTab] = useState("Upload");
	const { addPanorama } = useContext(ExperienceContext);
	const fileInput = useRef(null);
	const [uploadedFile, setUploadedFile] = useState(null);
	const [recentScenes, setRecentScenes] = useState("");
	const [pages, setPages] = useState(1);
	const [activePage, setPage] = useState(1);
	const [text, setText] = useState("");
	const [isPrimary, setIsPrimary] = useState(true);
	const [loading, setLoading] = useState(false);
	const handleFileUpload = (event) => {
		setUploadedFile(event.target.files[0]);
	};
	const handleCreate = async () => {
		if (text === "") {
			showErrorNotification("Scene label cannot be empty");
			return;
		} else if (!uploadedFile) {
			showErrorNotification("Please upload a panorama");
			return;
		}
		setLoading(true);
		const formData = new FormData();
		formData.append("myfile", uploadedFile);
		formData.append(
			"bodyData",
			JSON.stringify({
				UserId: user?.UserId,
				AccountId: user?.AccountId,
				Name: text,
				AssetTypeId: 3,
			})
		);
		const result = await uploadMedia(formData);
		if (result.status === 201) {
			addPanorama(
				text,
				result.data.res.LocationUrl,
				result.data.res.PreviewUrl,
				isPrimary
			);
			showSuccessNotification("Scene added");
		} else {
			showErrorNotification("Scene could not be added");
		}
		setIsAddModalOpen(false);
		setUploadedFile(null);
		setText("");
		setIsPrimary(true);
		closeControls();
		setLoading(false);
	};

	const addFromRecent = (pano) => {
		addPanorama(pano.Name, pano.LocationUrl, pano.PreviewUrl, true);
		setIsAddModalOpen(false);
		setUploadedFile(null);
		setText("");
		setIsPrimary(true);
		closeControls();
	};

	useEffect(() => {
		const recentMedia = async () => {
			const result = await getAssets(user?.UserId, activePage, 3, 12);
			setRecentScenes(result.data);
			setPages(result.totalPages);
		};
		recentMedia();
	}, [activePage]);
	return (
		<Modal
			centered
			size="lg"
			opened={isOpen}
			onClose={() => {
				setIsAddModalOpen(false);
				setText("");
				setUploadedFile(null);
				setIsPrimary(true);
			}}
			title="Add new scene"
			styles={{
				modal: { backgroundColor: "#040F1F" },
				title: { color: "#ffffff", fontWeight: "700" },
			}}
		>
			{loading && (
				<div className="LoadingModal">
					<Loader className="LoadingModal_loader" />
				</div>
			)}
			<Container>
				<Switch
					my={15}
					labelPosition="left"
					label="Is this scene primary?"
					styles={{
						label: {
							color: "#ffffff",
						},
					}}
					checked={isPrimary}
					onChange={(event) =>
						setIsPrimary(event.currentTarget.checked)
					}
				/>
				<TextInput
					my={5}
					label="Scene Name"
					variant="filled"
					placeholder="Enter name"
					styles={{
						root: {
							display: "flex",
							alignItems: "center",
							gap: "10px",
						},
						input: {
							backgroundColor: "#040F1F",
							border: "1px solid #09254E",
						},
						label: {
							color: "#ffffff",
							fontWeight: "500",
							marginBottom: "0px",
						},
					}}
					value={text}
					onChange={(event) => setText(event.currentTarget.value)}
				/>
			</Container>
			<Tabs
				value={activeTab}
				onTabChange={setActiveTab}
				style={{ borderBottom: "none !important" }}
				styles={{
					tabsListWrapper: { borderBottom: "none !important" },
					tabsList: { borderBottom: "none !important" },
				}}
			>
				<Tabs.List>
					<Tabs.Tab
						value="Upload"
						sx={{
							"&:hover": {
								backgroundColor: "inherit",
								borderBottom: "none",
							},
						}}
					>
						Upload
					</Tabs.Tab>
					<Tabs.Tab
						value="Recent"
						sx={{
							"&:hover": {
								backgroundColor: "inherit",
								borderBottom: "none",
							},
						}}
					>
						Recent
					</Tabs.Tab>
				</Tabs.List>
				<Tabs.Panel value="Upload" pt="xs">
					<Container style={{ width: "100%", minHeight: "300px" }}>
						{uploadedFile === null ? (
							<>
								<label
									htmlFor="file-input"
									style={{
										display: "flex",
										width: "100%",
										height: "100%",
										minHeight: "300px",
										alignItems: "center",
										justifyContent: "center",
										flexDirection: "column",
										gap: "10px",
										border: "1px solid #09254E",
										borderRadius: "5px",
										cursor: "pointer",
									}}
								>
									<Image src={uploadIcon} width={75} />
									<Button
										variant="outline"
										my={10}
										px={30}
										size="xs"
										radius={"md"}
										sx={(theme) => ({
											color: theme.colors.white[0],
											borderColor:
												theme.colors.ivisit_light[0],
										})}
										onClick={() => {
											fileInput.current.click();
										}}
									>
										Browse
									</Button>
								</label>
								<Input
									id="file-input"
									ref={fileInput}
									type="file"
									accept=".jpg, .jpeg, .png"
									variant="unstyled"
									styles={{
										wrapper: {
											display: "none",
										},
									}}
									onChange={handleFileUpload}
								/>
							</>
						) : (
							<Image
								height={220}
								src={URL.createObjectURL(uploadedFile)}
								fit="contain"
							/>
						)}
					</Container>
				</Tabs.Panel>
				<Tabs.Panel value="Recent" pt="xs">
					<ScrollArea
						mt={10}
						style={{ height: 300 }}
						type="auto"
						offsetScrollbars
						scrollbarSize={8}
						scrollHideDelay={500}
					>
						<Grid style={{ minHeight: 270 }}>
							{recentScenes &&
								recentScenes.map((item) => {
									return (
										<Grid.Col
											span={3}
											key={item.id}
											onClick={() => {
												addFromRecent(item);
											}}
										>
											<a
												style={{
													cursor: "pointer",
													display: "flex",
													flexDirection: "column",
												}}
											>
												<img
													alt="RecentPano"
													style={{
														width: "100%",
														height: "80px",
													}}
													src={
														CDN_URL +
														item.PreviewUrl
													}
												/>
												<p
													style={{
														textAlign: "center",
														textOverflow:
															"ellipsis",
														whiteSpace: "nowrap",
														fontSize: "10px",
														marginTop: "3px",
														overflow: "hidden",
													}}
												>
													{item.Name}
												</p>
											</a>
										</Grid.Col>
									);
								})}
						</Grid>
						{pages !== 1 && (
							<Pagination
								sx={{
									Button: {
										background: "#09254E",
										fontWeight: "600",
										padding: "0px 4px",
										fontSize: "12px",
										border: "none",
									},
									"Button:first-child": {
										backgroundColor: "#14B8E1",
										border: "none",
									},
									"Button:last-child": {
										backgroundColor: "#14B8E1",
										border: "none",
									},
								}}
								page={activePage}
								onChange={setPage}
								mt={5}
								total={pages}
								size="xs"
								style={{
									justifyContent: "center",
									color: "pink",
								}}
							/>
						)}
					</ScrollArea>
				</Tabs.Panel>
			</Tabs>
			<Group
				style={{
					width: "100%",
					justifyContent: "space-between",
					marginBotom: "15px",
				}}
			>
				<Group
					mt={5}
					style={{
						width: "auto",
						display: "flex",
						flexDirection: "column",
						alignItems: "flex-start",
						color: "#a4bac6",
						opacity: "0.5",
					}}
				>
					<Text
						size="xs"
						backgroundColor="red"
						color="white"
						style={{ lineHeight: "0px" }}
					>
						{" "}
						Make sure your file size is under 3 MB.
					</Text>
					<Text size="xs" color="white" style={{ lineHeight: "0px" }}>
						Learn More about our platform supported formats and
						restrictions.
					</Text>
				</Group>
				<Group
					mt={5}
					style={{
						width: "auto",
						display: "flex",
						justifyContent: "space-between",
					}}
				>
					<Button
						variant="outline"
						my={0}
						px={25}
						size="xs"
						radius={"md"}
						sx={(theme) => ({
							color: theme.colors.white[0],
							borderColor: theme.colors.ivisit_light[0],
						})}
						onClick={() => {
							setText("");
							setUploadedFile(null);
							setIsAddModalOpen(false);
						}}
					>
						Cancel
					</Button>
					<Button
						variant="outline"
						my={0}
						px={25}
						size="xs"
						radius={"md"}
						sx={(theme) => ({
							color: theme.colors.white[0],
							borderColor: theme.colors.ivisit_light[0],
						})}
						onClick={handleCreate}
					>
						Create
					</Button>
				</Group>
			</Group>
		</Modal>
	);
}

export default AddPanoModal;
